// messika-pwa#34 : bascule de canal et installation des nouvelles versions de la PWA.
import usePwaUpdateStore from '../stores/pwaUpdate'
import usePrefStore from '../stores/prefs'
import { channelCookieString, channelFromPref, decideCheckpoint, decideSwitchOutcome, legacyChannelCookieDeletion } from './pwaChannel'

const TRIED_KEY = 'ome_channel_tried'   // dernier canal dont la bascule a été tentée (anti-boucle)
const GUARD_DELAY = 20_000   // rechargement au-delà, même sans controllerchange

export const BUILD_VERSION: string = typeof __PWA_APP_VERSION__ !== 'undefined' ? __PWA_APP_VERSION__ : ''
export const BUILD_CHANNEL: number = channelFromPref(typeof __PWA_CANAL__ !== 'undefined' ? __PWA_CANAL__ : 1)

let registration: ServiceWorkerRegistration | undefined
// Contrôleur présent au chargement : seule une CHANGEMENT de contrôleur signale une nouvelle version
// (la 1re installation fait aussi passer controller de null à un SW).
let hadController = typeof navigator !== 'undefined' && !!navigator.serviceWorker?.controller
// Fenêtre « démarrage » : une version détectée pendant qu'elle est ouverte est installée d'office.
let startupWindowOpen = true

// Tentative de bascule non consommée faute de registration : rejouée dans setRegistration.
// On retient le contexte (session ou non) du point de contrôle d'origine.
let switchDeferred: { hasSession: boolean } | null = null

export function setRegistration(r: ServiceWorkerRegistration | undefined) {
   registration = r
   if (r && switchDeferred && startupWindowOpen) {
      const { hasSession } = switchDeferred
      switchDeferred = null
      void runUpdateCheckpoint(hasSession)
   }
}

/** Version + canal pour affichage, ex. « 1.4.10 · chan2 » (canal 1 : version seule). */
export function buildLabel(): string {
   return BUILD_CHANNEL > 1 ? `${BUILD_VERSION} · chan${BUILD_CHANNEL}` : BUILD_VERSION
}

function waitControllerChangeOrGuard(): Promise<void> {
   return new Promise((resolve) => {
      const sw = navigator.serviceWorker
      const done = () => { sw?.removeEventListener('controllerchange', done); clearTimeout(t); resolve() }
      const t = setTimeout(done, GUARD_DELAY)
      sw?.addEventListener('controllerchange', done)
   })
}

/** Overlay bloquant, contrôle de mise à jour, attente du nouveau SW, rechargement. */
export async function applyUpdateAndReload(alreadyActive = false): Promise<void> {
   if (usePwaUpdateStore.getState().updating) return
   const active = alreadyActive || usePwaUpdateStore.getState().updateAvailable   // nouveau SW déjà aux commandes
   usePwaUpdateStore.setState({ updating: true })
   if (!active) {
      const changed = waitControllerChangeOrGuard()
      try { await registration?.update() } catch (e) { console.warn('SW update KO', e) }
      await changed
   }
   window.location.reload()
}

function readTried(): number | null {
   try {
      const v = sessionStorage.getItem(TRIED_KEY)
      return v === null ? null : channelFromPref(v)
   } catch { return null }
}

function writeTried(n: number | null) {
   try {
      if (n === null) sessionStorage.removeItem(TRIED_KEY)
      else sessionStorage.setItem(TRIED_KEY, String(n))
   } catch { /* stockage indisponible : pas de garde anti-boucle */ }
}

/** Pose le cookie d'aiguillage nginx puis bascule ; abandon immédiat si aucun nouveau SW n'est servi. */
export async function switchChannelAndReload(wanted: number, hasSession = true): Promise<void> {
   if (decideSwitchOutcome(registration) === 'defer') { switchDeferred = { hasSession }; return }
   if (usePwaUpdateStore.getState().updating) return
   writeTried(wanted)
   document.cookie = channelCookieString(wanted)
   document.cookie = legacyChannelCookieDeletion()
   usePwaUpdateStore.setState({ updating: true })
   const changed = waitControllerChangeOrGuard()
   try { await registration?.update() } catch (e) { console.warn('SW update KO', e) }
   if (decideSwitchOutcome(registration) === 'abandon') {
      // Canal non déployé : nginx resert le build chargé, le cookie redevient cohérent avec lui.
      document.cookie = channelCookieString(BUILD_CHANNEL)
      usePwaUpdateStore.setState({ updating: false })
      return
   }
   await changed
   window.location.reload()
}

/**
 * Point de contrôle (démarrage / fin de synchro du login) : bascule de canal si besoin,
 * sinon installe d'office une nouvelle version déjà détectée. `hasSession` faux : la pref est
 * inconnue, aucune bascule (messika-pwa#39).
 */
export async function runUpdateCheckpoint(hasSession = true): Promise<void> {
   const wanted = usePrefStore.getState().pwaCanal ?? 1
   const d = decideCheckpoint({
      buildChannel: BUILD_CHANNEL,
      wantedChannel: wanted,
      lastTried: readTried(),
      hasSession,
      updateAvailable: usePwaUpdateStore.getState().updateAvailable,
   })
   if (d.clearTried) writeTried(null)
   if (d.action === 'switch') await switchChannelAndReload(wanted, hasSession)
   else if (d.action === 'install') await applyUpdateAndReload()
}

/** Fin de la fenêtre de démarrage (échéance de 30 s, voir watchNewVersions). */
function endStartupWindow() {
   startupWindowOpen = false
}

/** Écoute le changement de contrôleur : nouvelle version du même canal. */
export function watchNewVersions(): () => void {
   const sw = typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined
   if (!sw) return () => { }
   const onChange = () => {
      if (!hadController) { hadController = true; return }   // 1re installation
      if (usePwaUpdateStore.getState().updating) return      // déjà pris en charge
      if (startupWindowOpen) {
         void applyUpdateAndReload(true)
      } else {
         usePwaUpdateStore.setState({ updateAvailable: true })
      }
   }
   sw.addEventListener('controllerchange', onChange)
   const t = setTimeout(endStartupWindow, 30_000)   // filet : hors ligne, le point de contrôle du démarrage peut ne jamais venir
   return () => { clearTimeout(t); sw.removeEventListener('controllerchange', onChange) }
}
